import { Map, List } from 'immutable'
import PropTypes from 'prop-types'
import { truncate } from 'theme/web-app'
import ImmutablePropTypes from 'react-immutable-proptypes'
import React, { useMemo, useCallback } from 'react'
import NotAvailable, { TYPE_NOT_AVAILABLE_OVERLAY } from 'components/NotAvailable'
import WatchAccess, { ACCESS_CHECK_SHARE_FEATURE_GEO } from 'components/WatchAccess'
import WatchAccessAllowed from 'components/WatchAccessAllowed'
import WatchAccessDenied from 'components/WatchAccessDenied'
import { ICON_TYPES } from 'components/Icon.v2'
import { getAuthIsLoggedIn } from 'services/auth'
import { H5 } from 'components/Heading'
import Row from 'components/Row.v2'
import Button, { TYPES } from 'components/Button'
import { useDispatch } from 'react-redux'
import { get as getConfig } from 'config'
import { getShareV3Link } from 'services/share'
import { isEntitled } from 'services/subscription'

const isShareV3Enabled = !!getConfig().features.sharePageV3

function getQuery (isMember, previewAvailable) {
  const featureOrPreview = isMember ? 'feature' : 'preview'
  return !isMember && !previewAvailable ? {} : { fullplayer: featureOrPreview }
}

function handlePlay (onWatch, nid) {
  onWatch(nid)
}

function createAccessor (Accessor, item) {
  if (!item) return null
  const url = item.get('url', '') || ''
  const title = item.get('title', '')
  return <Accessor title={title} to={url} />
}

function renderItem (item, params, props, onWatch, dispatch) {
  const {
    auth,
    kind,
    staticText,
    language,
    portal,
    user,
  } = props
  const { focused } = params

  if (!item) {
    return <div className="portal-playlist-v2__item" />
  }

  const preview = item.get('preview', Map())
  const feature = item.get('feature', Map())
  const isMember = getAuthIsLoggedIn(auth)
  const isFree = item.get('isFree')
  const title = item.get('title')
  const previewAvailable = item.getIn(['preview', 'id'], -1) > -1
  const description = item.get('body')
  const image = item.get('imageWithText')
  const isSharedAllowed = item.getIn(['feature', 'shareAllowed'], false)
  const isUserEntitled = isEntitled(auth.get('subscriptions', List()))
  const descriptionSplit = truncate(description, 100)
  const isShareItem = isSharedAllowed && !isUserEntitled

  const url = isShareV3Enabled && isShareItem
    ? getShareV3Link({
      userLang: language,
      userRfd: portal.getIn(['data', 'userReferralId']),
      userAffId: portal.getIn(['data', 'everflowAffiliateId']),
      userUid: portal.getIn(['data', 'uuid']),
      contentUrl: item.get('url'),
      contentId: item.get('id'),
      contentType: item.get('contentType'),
    })
    : item.get('url')

  const imageTag = (
    <img
      className="portal-playlist-v2__image"
      alt={title}
      src={image}
    />
  )

  return (
    <div className="portal-playlist-v2__item">
      <WatchAccess
        accessCheck={ACCESS_CHECK_SHARE_FEATURE_GEO}
        feature={feature}
        preview={preview}
        auth={auth}
        user={user}
      >
        <WatchAccessDenied>
          <NotAvailable
            message={staticText.get('notAvailableInRegion')}
            type={TYPE_NOT_AVAILABLE_OVERLAY}
          />
          {imageTag}
        </WatchAccessDenied>
        <WatchAccessAllowed>
          {isShareV3Enabled
            ? <Button
              className="portal-playlist-v2__item-btn"
              url={url}
              directLink={isShareItem}
            >
              {imageTag}
            </Button>
            : <Button
              className="portal-playlist-v2__item-btn"
              onClick={isFree ? () => handlePlay(onWatch, item.get('nid'), kind, dispatch) : () => {}}
              query={isFree ? {} : getQuery(isMember, previewAvailable)}
              url={isFree ? '' : url}
            >
              {isFree
                ? <div className="portal-playlist-v2__free">
                  {staticText.get('free')}
                </div>
                : null
              }
              {imageTag}
            </Button>
          }
          {focused
            ? <div className="portal-playlist-v2__meta">
              <Button
                className="portal-playlist-v2__meta-link"
                type={TYPES.LINK}
                url={url}
                directLink={isShareItem}
              />
              <H5>{title}</H5>
              <p className="portal-playlist-v2__body">
                {descriptionSplit}
              </p>
            </div>
            : null
          }
        </WatchAccessAllowed>
      </WatchAccess>
    </div>
  )
}

export default function Playlist (props) {
  const {
    getPortalShare,
    ctaOnClick,
    ctaLabel,
    editable,
    setMode,
    portal,
    modes,
    light,
    label,
    items,
    auth,
  } = props
  const dispatch = useDispatch()

  const isPortalOwner = portal.get('isPortalOwner')

  const vars = useMemo(() => ({
    className: `portal-playlist-v2 ${light ? 'portal-playlist-v2--light' : ''}`,
  }), [light])

  const onCreateFreeVideo = useCallback((contentId) => {
    getPortalShare({ contentId })
  }, [])

  const editPlaylist = useCallback(() => {
    setMode(modes.PLAYLIST_EDIT)
  }, [])

  return (
    <section className={vars.className}>
      <Row
        className="portal-playlist-v2__row"
        createAccessor={createAccessor}
        ctaOnClick={ctaOnClick}
        ctaLabel={ctaLabel}
        items={items}
        label={label}
        auth={auth}
        inverted
      >{(item, data) => renderItem(item, data, props, onCreateFreeVideo, dispatch)}
      </Row>
      {isPortalOwner && editable ? (
        <div className="portal-playlist-v2__actions">
          <Button
            className="portal-playlist-v2__action-edit"
            type={TYPES.ICON_PRIMARY}
            icon={ICON_TYPES.PENCIL}
            onClick={editPlaylist}
            shadow
          />
        </div>
      ) : null}
    </section>
  )
}

Playlist.propTypes = {
  staticText: ImmutablePropTypes.map.isRequired,
  items: ImmutablePropTypes.list.isRequired,
  portal: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  label: PropTypes.string.isRequired,
  kind: PropTypes.string.isRequired,
  getPortalShare: PropTypes.func,
  ctaOnClick: PropTypes.func,
  ctaLabel: PropTypes.string,
  editable: PropTypes.bool,
  light: PropTypes.bool,
  language: PropTypes.string.isRequired,
}
